import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import WorkspaceDataTable from '../../../components/workspace/WorkspaceDataTable';
import WorkspacePageHeader from '../../../components/workspace/WorkspacePageHeader';
import { useAdminRecordDelete } from '../../../components/workspace/useAdminRecordDelete';
import { revisionColumn, useMasterRevision } from '../../../components/workspace/useMasterRevision';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import './CustomerMaster.scss';

const TenderTypeListPage = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useDocumentTitle('Tender types');

  const load = useCallback(async () => {
    setError('');
    try {
      const response = await parivartanAPI.listTenderTypes();
      setRows(response.data?.data?.tenderTypes || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load tender types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      { key: 'typeNo', label: 'Type no.', width: '140px', mobileTitle: true },
      { key: 'code', label: 'Code', width: '90px' },
      {
        key: 'name',
        label: 'Name',
        render: (value, row) => (
          <div className="cust-name">
            <span className="cust-name__title">{value}</span>
            {row.submission ? <span className="cust-name__meta">{row.submission}</span> : null}
          </div>
        )
      },
      { key: 'group', label: 'Group', width: '170px' },
      revisionColumn,
      {
        key: 'status',
        label: 'Status',
        width: '120px',
        render: (value) => (
          <span className={`cust-status cust-status--${value === 'active' ? 'active' : 'inactive'}`}>
            {value === 'active' ? 'Active' : 'Inactive'}
          </span>
        )
      }
    ],
    []
  );

  const admin = useAdminRecordDelete({
    entity: 'tenderType',
    noun: 'tender type',
    plural: 'tender types',
    reload: load,
    label: (row) => row.name
  });
  const revision = useMasterRevision({ entity: 'tenderType' });

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Navigate to="/apps/tender" replace />;
  }

  if (loading) return <Spinner animation="border" size="sm" />;

  return (
    <div className="cust-page">
      <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters')}>
        <BiArrowBack />
        Masters
      </button>
      <WorkspacePageHeader
        title="Tender types"
        help="tenderTypes"
        subtitle="The forms a bid can take. Choose one of these when a tender is captured."
        actions={(
          <>
            <span className="cust-page__count">Total records {rows.length}</span>
            <Button size="sm" variant="primary" onClick={() => navigate('/apps/tender/masters/tender-types/new')}>
              New
            </Button>
          </>
        )}
      />
      {error ? <Alert variant="danger">{error}</Alert> : null}
      {admin.notice}
      <WorkspaceDataTable
        downloadPermission="masters.download"
        title="Tender types"
        data={rows}
        columns={columns}
        searchPlaceholder="Search number, code, name, group…"
        pageSize={12}
        onView={(row) => navigate(`/apps/tender/masters/tender-types/${row._id}`)}
        onEdit={(row) => navigate(`/apps/tender/masters/tender-types/${row._id}/edit`)}
        {...admin.tableProps}
        rowActions={revision.wrapActions(admin.wrapActions(() => [
          { type: 'view', label: 'View' },
          { type: 'edit', label: 'Edit' },
          { type: 'delete', label: 'Delete', variant: 'danger' }
        ]))}
      />
      {admin.modal}
      {revision.modals}
    </div>
  );
};

export default TenderTypeListPage;
